import { useState, useEffect } from 'react';
import {
  TrendingUp, Settings, Upload, GitBranch, Megaphone, Lightbulb, Layers, Search,
  BarChart3, ChevronDown, ChevronRight, Compass, BookOpen,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import GlossarySheet from './GlossarySheet';

/**
 * Sidebar with grouped navigation.
 *
 * Structure:
 *   - Performance Analysis  ▼   (collapsible parent group)
 *       Funnel Breakdown        (route /funnel — was "Funnel Split")
 *       Ad Format Breakdown     (route /ad-properties — was "Ad Properties")
 *   - Search Trends
 *   - Ads Superset
 *   - Upload Center
 *   - Funnel Mapping
 *   - Admin Panel (admin only)
 *
 * Routes are preserved — only labels + grouping changed. The parent group is
 * collapsible via chevron, defaults to expanded, and stays expanded whenever
 * one of its children is the active page (so the user is never lost in a
 * collapsed section).
 */

// Persisted expand/collapse state — feels more polished than resetting on every
// nav click. Falls back to expanded on first ever visit.
const STORAGE_KEY = 'trace_sidebar_perfanalysis_open';

export default function Sidebar({ user, currentPage }) {
  const navigate = useNavigate();

  const perfChildren = [
    { id: 'funnel', label: 'Funnel Breakdown', icon: TrendingUp, path: '/funnel' },
    { id: 'ad-properties', label: 'Ad Format Breakdown', icon: Megaphone, path: '/ad-properties' },
    { id: 'exploration', label: 'Exploration Report', icon: Compass, path: '/exploration' },
  ];
  const perfActive = perfChildren.some(c => c.id === currentPage);

  const [perfOpen, setPerfOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? true : stored === '1';
    } catch { return true; }
  });
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  // Auto-expand whenever a child becomes active — never hide the user's own
  // current page behind a collapsed parent.
  useEffect(() => {
    if (perfActive && !perfOpen) setPerfOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfActive]);

  const togglePerf = () => {
    setPerfOpen(v => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const flatItems = [
    { id: 'search-trends', label: 'Search Trends', icon: Search, path: '/search-trends' },
    { id: 'ads-superset', label: 'Ads Superset', icon: Layers, path: '/ads-superset' },
    { id: 'upload', label: 'Upload Center', icon: Upload, path: '/upload' },
    { id: 'funnel-mapping', label: 'Funnel Mapping', icon: GitBranch, path: '/funnel-mapping' },
  ];
  if (user?.role === 'admin') {
    flatItems.push({ id: 'admin', label: 'Admin Panel', icon: Settings, path: '/admin' });
  }

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col min-h-screen">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#32B4AC] rounded-lg flex items-center justify-center text-white font-bold text-lg">T</div>
          <div>
            <h2 className="text-xl font-bold text-[#32B4AC] tracking-tight leading-tight">TRACE</h2>
            <p className="text-[10px] text-gray-400 tracking-wide">by <span className="text-gray-500 font-medium italic">wellversed</span><sup className="text-[7px]">TM</sup></p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {/* Performance Analysis — collapsible parent group */}
        <button
          type="button"
          onClick={togglePerf}
          aria-expanded={perfOpen}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
            perfActive
              ? 'text-[#0F0F0F] bg-[#32B4AC]/8 font-semibold'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          data-testid="nav-perf-analysis"
        >
          <BarChart3 className={`w-5 h-5 ${perfActive ? 'text-[#32B4AC]' : ''}`} />
          <span className="text-sm flex-1 text-left">Performance Analysis</span>
          {perfOpen
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>

        {perfOpen && (
          <div className="ml-3 pl-3 space-y-1 border-l border-gray-200" data-testid="nav-perf-children">
            {perfChildren.map(child => {
              const Icon = child.icon;
              const isActive = currentPage === child.id;
              return (
                <button
                  key={child.id}
                  onClick={() => navigate(child.path)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md transition-all duration-200 ${
                    isActive
                      ? 'bg-[#32B4AC] text-white font-semibold shadow-sm shadow-[#32B4AC]/20'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${child.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-[13px]">{child.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Flat items */}
        {flatItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-[#32B4AC] text-white font-semibold shadow-md shadow-[#32B4AC]/20'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Control Panel + Glossary + Request a Feature */}
      <div className="px-4 pb-4 space-y-2">
        <button
          onClick={() => navigate('/control-panel')}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
          data-testid="open-control-panel-btn"
        >
          <Settings className="w-4 h-4" />
          <span className="text-sm font-medium">Control Panel</span>
        </button>
        <button
          onClick={() => setGlossaryOpen(true)}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
          data-testid="open-glossary-btn"
        >
          <BookOpen className="w-4 h-4" />
          <span className="text-sm font-medium">Glossary</span>
        </button>
        <button
          onClick={() => toast.info('Feature request noted! We will get back to you.')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors duration-200 border border-amber-200"
          data-testid="request-feature-btn"
        >
          <Lightbulb className="w-5 h-5" />
          <span className="text-sm font-medium">Request a Feature</span>
        </button>
      </div>
      <GlossarySheet open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
    </div>
  );
}
